import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAcademicSessions,
  selectAllSessions,
} from "../../../Slices/sessionsSlice";
import {
  MonetizationOn,
  CalendarMonth,
  Grade,
  Money,
} from "@mui/icons-material";
import "../../../Styles/dashboard.css";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  {
    id: "academicSession",
    label: "Academic Session",
    icon: <CalendarMonth sx={{ marginRight: 1 }} />,
  },
  {
    id: "feeTypes",
    label: "Fee Type",
    icon: <Money sx={{ marginRight: 1 }} />,
  },
  { id: "grade", label: "Grade", icon: <Grade sx={{ marginRight: 1 }} /> },
  {
    id: "amount",
    label: "Amount",
    icon: <MonetizationOn sx={{ marginRight: 1 }} />,
  },
];

function FeesTable({ feesDetails }) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchAcademicSessions());
  }, [dispatch]);
  const [loading, setLoading] = useState(false);
  const [paginatedData, setPaginatedData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const sessions = useSelector(selectAllSessions);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(feesDetails?.data);
  }, [feesDetails]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell className="table-cell">
                        {
                          sessions?.data?.find(
                            (session) => session.id === row?.academicSessionId
                          )?.name
                        }
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {row?.feeType?.name}
                      </TableCell>
                      <TableCell className="table-cell">
                        {row?.grade === null ? "All" : row?.grade?.name}
                      </TableCell>
                      <TableCell className="table-cell">
                        {row?.amount?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

FeesTable.propTypes = {
  feesDetails: PropTypes.object.isRequired,
  feeTypes: PropTypes.object.isRequired,
  sessions: PropTypes.object.isRequired,
  grades: PropTypes.object.isRequired,
};

export default FeesTable;
